import React, { useState, useEffect } from 'react';
import { mailAPI } from '../../../services/api';
import { toast } from 'react-hot-toast';
import './MailManagementTab.css';

const MailManagementTab = () => {
  const [activeSubTab, setActiveSubTab] = useState('accounts');
  const [accounts, setAccounts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [apiStatus, setApiStatus] = useState(null);
  
  // Inbox state
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [inbox, setInbox] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loadingInbox, setLoadingInbox] = useState(false);
  
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  const [accountForm, setAccountForm] = useState({
    email: '',
    password: '',
    display_name: '',
    auto_reply_enabled: true
  });
  
  const [templateForm, setTemplateForm] = useState({
    mail_account_id: '',
    subject: 'Re: {{original_subject}}',
    body: '',
    is_html: false,
    is_active: true
  });

  useEffect(() => {
    checkApiStatus();
    loadData();
  }, []);

  useEffect(() => {
    if (activeSubTab !== 'inbox') {
      setSelectedAccount(null);
      setSelectedMessage(null);
    }
    loadData();
  }, [activeSubTab]);

  const checkApiStatus = async () => {
    try {
      const response = await mailAPI.health();
      setApiStatus(response.data);
    } catch (error) {
      setApiStatus({ status: 'error' });
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeSubTab === 'accounts' || activeSubTab === 'inbox') {
        const res = await mailAPI.getAccounts();
        setAccounts(res.data);
      } else if (activeSubTab === 'templates') {
        const [templatesRes, accountsRes] = await Promise.all([
          mailAPI.getTemplates(),
          mailAPI.getAccounts()
        ]);
        setTemplates(templatesRes.data);
        setAccounts(accountsRes.data);
      } else if (activeSubTab === 'logs') {
        const [logsRes, statsRes] = await Promise.all([
          mailAPI.getLogs({ limit: 100 }),
          mailAPI.getStats()
        ]);
        setLogs(logsRes.data.data || []);
        setStats(statsRes.data);
      }
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Inbox fonksiyonları
  const loadInbox = async (account) => {
    setSelectedAccount(account);
    setSelectedMessage(null);
    setLoadingInbox(true);
    try {
      const res = await mailAPI.getInbox(account.id);
      setInbox(res.data.messages || []);
    } catch (error) {
      toast.error('Gelen kutusu yüklenemedi');
    } finally {
      setLoadingInbox(false);
    }
  };

  const loadMessage = async (msg) => {
    setLoadingInbox(true);
    try {
      const res = await mailAPI.getMessage(selectedAccount.id, msg.id);
      setSelectedMessage(res.data);
    } catch (error) {
      toast.error('Mail yüklenemedi');
    } finally {
      setLoadingInbox(false);
    }
  };

  const deleteMessage = async (msg) => {
    if (!window.confirm('Bu maili silmek istediğinize emin misiniz?')) return;
    try {
      await mailAPI.deleteMessage(selectedAccount.id, msg.id);
      toast.success('Mail silindi');
      loadInbox(selectedAccount);
      setSelectedMessage(null);
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    if (!accountForm.email || (!editingAccount && !accountForm.password)) {
      toast.error('Email ve şifre zorunludur');
      return;
    }

    const emailToSend = accountForm.email.includes('@') 
      ? accountForm.email 
      : accountForm.email + '@kralsosyal.com';

    try {
      if (editingAccount) {
        const updateData = { ...accountForm, email: emailToSend };
        if (!updateData.password) delete updateData.password;
        await mailAPI.updateAccount(editingAccount.id, updateData);
        toast.success('Mail hesabı güncellendi');
      } else {
        await mailAPI.createAccount({ ...accountForm, email: emailToSend });
        toast.success('Mail hesabı oluşturuldu');
      }
      setShowAccountModal(false);
      resetAccountForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'İşlem başarısız');
    }
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm('Bu mail hesabını silmek istediğinize emin misiniz?')) return;
    try {
      await mailAPI.deleteAccount(id);
      toast.success('Mail hesabı silindi');
      loadData();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const handleEditAccount = (account) => {
    setAccountForm({
      email: account.email.replace('@kralsosyal.com', ''),
      password: '',
      display_name: account.display_name || '',
      auto_reply_enabled: account.auto_reply_enabled === 1
    });
    setEditingAccount(account);
    setShowAccountModal(true);
  };

  const resetAccountForm = () => {
    setAccountForm({ email: '', password: '', display_name: '', auto_reply_enabled: true });
    setEditingAccount(null);
  };

  const handleTemplateSubmit = async (e) => {
    e.preventDefault();
    if (!templateForm.mail_account_id || !templateForm.body) {
      toast.error('Mail hesabı ve içerik zorunludur');
      return;
    }

    try {
      if (editingTemplate) {
        await mailAPI.updateTemplate(editingTemplate.id, templateForm);
        toast.success('Şablon güncellendi');
      } else {
        await mailAPI.createTemplate(templateForm);
        toast.success('Şablon oluşturuldu');
      }
      setShowTemplateModal(false);
      resetTemplateForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'İşlem başarısız');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Bu şablonu silmek istediğinize emin misiniz?')) return;
    try {
      await mailAPI.deleteTemplate(id);
      toast.success('Şablon silindi');
      loadData();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const handleEditTemplate = (template) => {
    setTemplateForm({
      mail_account_id: template.mail_account_id,
      subject: template.subject,
      body: template.body,
      is_html: template.is_html === 1,
      is_active: template.is_active === 1
    });
    setEditingTemplate(template);
    setShowTemplateModal(true);
  };

  const resetTemplateForm = () => {
    setTemplateForm({
      mail_account_id: '',
      subject: 'Re: {{original_subject}}',
      body: '',
      is_html: false,
      is_active: true
    });
    setEditingTemplate(null);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 86400000) { // 24 saat
      return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    } else if (diff < 604800000) { // 7 gün
      return date.toLocaleDateString('tr-TR', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="mail-management-tab">
      <div className="mail-header">
        <h2>📧 Mail Yönetimi</h2>
        <div className="mail-api-status">
          <span className={`mail-status-dot ${apiStatus?.status === 'ok' ? 'online' : 'offline'}`}></span>
          {apiStatus?.status === 'ok' ? 'Bağlı' : 'Bağlantı Yok'}
        </div>
      </div>

      <div className="mail-sub-tabs">
        <button className={activeSubTab === 'accounts' ? 'active' : ''} onClick={() => setActiveSubTab('accounts')}>
          👤 Hesaplar
        </button>
        <button className={activeSubTab === 'inbox' ? 'active' : ''} onClick={() => setActiveSubTab('inbox')}>
          📥 Gelen Kutusu
        </button>
        <button className={activeSubTab === 'templates' ? 'active' : ''} onClick={() => setActiveSubTab('templates')}>
          📝 Şablonlar
        </button>
        <button className={activeSubTab === 'logs' ? 'active' : ''} onClick={() => setActiveSubTab('logs')}>
          📊 Loglar
        </button>
      </div>

      {loading ? (
        <div className="mail-loading">Yükleniyor...</div>
      ) : (
        <div className="mail-content">
          {/* ACCOUNTS TAB */}
          {activeSubTab === 'accounts' && (
            <div className="mail-section">
              <div className="mail-section-header">
                <h3>Mail Hesapları ({accounts.length})</h3>
                <button className="mail-btn-primary" onClick={() => { resetAccountForm(); setShowAccountModal(true); }}>
                  + Yeni Hesap
                </button>
              </div>
              <div className="mail-table-wrapper">
                <table className="mail-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Görünen Ad</th>
                      <th>Auto-Reply</th>
                      <th>Tarih</th>
                      <th>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map(account => (
                      <tr key={account.id}>
                        <td><strong>{account.email}</strong></td>
                        <td>{account.display_name || '-'}</td>
                        <td>
                          <span className={`mail-badge ${account.auto_reply_enabled ? 'success' : 'secondary'}`}>
                            {account.auto_reply_enabled ? 'Aktif' : 'Pasif'}
                          </span>
                        </td>
                        <td>{new Date(account.created_at).toLocaleDateString('tr-TR')}</td>
                        <td>
                          <button className="mail-btn-icon" onClick={() => handleEditAccount(account)} title="Düzenle">✏️</button>
                          <button className="mail-btn-icon" onClick={() => { setActiveSubTab('inbox'); setTimeout(() => loadInbox(account), 100); }} title="Gelen Kutusu">📥</button>
                          <button className="mail-btn-icon danger" onClick={() => handleDeleteAccount(account.id)} title="Sil">🗑️</button>
                        </td>
                      </tr>
                    ))}
                    {accounts.length === 0 && (
                      <tr><td colSpan="5" className="mail-empty">Henüz hesap yok</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* INBOX TAB */}
          {activeSubTab === 'inbox' && (
            <div className="mail-section mail-inbox-section">
              {!selectedAccount ? (
                <div className="mail-inbox-account-list">
                  <h3>Hesap Seçin</h3>
                  <div className="mail-account-cards">
                    {accounts.map(account => (
                      <div key={account.id} className="mail-account-card" onClick={() => loadInbox(account)}>
                        <div className="mail-account-avatar">📧</div>
                        <div className="mail-account-info">
                          <strong>{account.email}</strong>
                          <span>{account.display_name || 'Mail Hesabı'}</span>
                        </div>
                        <span className="mail-account-arrow">→</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mail-inbox-container">
                  <div className="mail-inbox-header">
                    <button className="mail-btn-back" onClick={() => { setSelectedAccount(null); setSelectedMessage(null); }}>
                      ← Geri
                    </button>
                    <h3>{selectedAccount.email}</h3>
                    <button className="mail-btn-secondary" onClick={() => loadInbox(selectedAccount)}>🔄 Yenile</button>
                  </div>
                  
                  <div className="mail-inbox-layout">
                    <div className={`mail-inbox-list ${selectedMessage ? 'has-detail' : ''}`}>
                      {loadingInbox ? (
                        <div className="mail-loading">Yükleniyor...</div>
                      ) : inbox.length === 0 ? (
                        <div className="mail-empty-inbox">📭 Gelen kutusu boş</div>
                      ) : (
                        inbox.map(msg => (
                          <div 
                            key={msg.id} 
                            className={`mail-inbox-item ${selectedMessage?.id === msg.id ? 'active' : ''} ${msg.folder === 'new' ? 'unread' : ''}`}
                            onClick={() => loadMessage(msg)}
                          >
                            <div className="mail-inbox-item-header">
                              <span className="mail-inbox-from">{msg.fromAddress}</span>
                              <span className="mail-inbox-date">{formatDate(msg.date)}</span>
                            </div>
                            <div className="mail-inbox-subject">{msg.subject}</div>
                            <div className="mail-inbox-preview">{msg.preview}</div>
                          </div>
                        ))
                      )}
                    </div>
                    
                    {selectedMessage && (
                      <div className="mail-inbox-detail">
                        <div className="mail-message-header">
                          <div className="mail-message-meta">
                            <h4>{selectedMessage.subject}</h4>
                            <p><strong>Kimden:</strong> {selectedMessage.from}</p>
                            <p><strong>Kime:</strong> {selectedMessage.to}</p>
                            <p><strong>Tarih:</strong> {new Date(selectedMessage.date).toLocaleString('tr-TR')}</p>
                          </div>
                          <button className="mail-btn-icon danger" onClick={() => deleteMessage(selectedMessage)} title="Sil">🗑️</button>
                        </div>
                        <div className="mail-message-body">
                          {selectedMessage.html ? (
                            <div dangerouslySetInnerHTML={{ __html: selectedMessage.html }} />
                          ) : (
                            <pre>{selectedMessage.text}</pre>
                          )}
                        </div>
                        {selectedMessage.attachments?.length > 0 && (
                          <div className="mail-message-attachments">
                            <strong>Ekler:</strong>
                            {selectedMessage.attachments.map((att, i) => (
                              <span key={i} className="mail-attachment-item">📎 {att.filename}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TEMPLATES TAB */}
          {activeSubTab === 'templates' && (
            <div className="mail-section">
              <div className="mail-section-header">
                <h3>Auto-Reply Şablonları ({templates.length})</h3>
                <button className="mail-btn-primary" onClick={() => { resetTemplateForm(); setShowTemplateModal(true); }}>
                  + Yeni Şablon
                </button>
              </div>
              <div className="mail-templates-grid">
                {templates.map(template => (
                  <div key={template.id} className="mail-template-card">
                    <div className="mail-template-header">
                      <span className="mail-template-email">{template.email}</span>
                      <span className={`mail-badge ${template.is_active ? 'success' : 'secondary'}`}>
                        {template.is_active ? 'Aktif' : 'Pasif'}
                      </span>
                    </div>
                    <div className="mail-template-subject"><strong>Konu:</strong> {template.subject}</div>
                    <div className="mail-template-body">{template.body.substring(0, 100)}...</div>
                    <div className="mail-template-actions">
                      <button onClick={() => handleEditTemplate(template)}>Düzenle</button>
                      <button className="danger" onClick={() => handleDeleteTemplate(template.id)}>Sil</button>
                    </div>
                  </div>
                ))}
                {templates.length === 0 && <div className="mail-empty">Henüz şablon yok</div>}
              </div>
            </div>
          )}

          {/* LOGS TAB */}
          {activeSubTab === 'logs' && (
            <div className="mail-section">
              <div className="mail-section-header">
                <h3>Mail Logları</h3>
                <button className="mail-btn-secondary" onClick={loadData}>🔄 Yenile</button>
              </div>
              
              {stats && (
                <div className="mail-stats-grid">
                  <div className="mail-stat-card"><span className="mail-stat-icon">📨</span><span className="mail-stat-value">{stats.total}</span><span className="mail-stat-label">Toplam</span></div>
                  <div className="mail-stat-card"><span className="mail-stat-icon">📥</span><span className="mail-stat-value">{stats.incoming}</span><span className="mail-stat-label">Gelen</span></div>
                  <div className="mail-stat-card"><span className="mail-stat-icon">📤</span><span className="mail-stat-value">{stats.outgoing}</span><span className="mail-stat-label">Giden</span></div>
                  <div className="mail-stat-card success"><span className="mail-stat-icon">✅</span><span className="mail-stat-value">{stats.replied}</span><span className="mail-stat-label">Yanıtlanan</span></div>
                  <div className="mail-stat-card danger"><span className="mail-stat-icon">❌</span><span className="mail-stat-value">{stats.failed}</span><span className="mail-stat-label">Başarısız</span></div>
                </div>
              )}

              <div className="mail-table-wrapper">
                <table className="mail-table">
                  <thead>
                    <tr>
                      <th>Tarih</th>
                      <th>Yön</th>
                      <th>Kimden</th>
                      <th>Kime</th>
                      <th>Konu</th>
                      <th>Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => (
                      <tr key={log.id}>
                        <td>{new Date(log.created_at).toLocaleString('tr-TR')}</td>
                        <td><span className={`mail-badge ${log.direction}`}>{log.direction === 'incoming' ? '📥 Gelen' : '📤 Giden'}</span></td>
                        <td>{log.from_email}</td>
                        <td>{log.to_email}</td>
                        <td>{log.subject || '-'}</td>
                        <td><span className={`mail-badge ${log.status}`}>{log.status}</span></td>
                      </tr>
                    ))}
                    {logs.length === 0 && <tr><td colSpan="6" className="mail-empty">Henüz log yok</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ACCOUNT MODAL */}
      {showAccountModal && (
        <div className="mail-modal-overlay" onClick={() => setShowAccountModal(false)}>
          <div className="mail-modal-content" onClick={e => e.stopPropagation()}>
            <div className="mail-modal-header">
              <h3>{editingAccount ? 'Hesap Düzenle' : 'Yeni Mail Hesabı'}</h3>
              <button className="mail-modal-close" onClick={() => setShowAccountModal(false)}>×</button>
            </div>
            <form onSubmit={handleAccountSubmit}>
              <div className="mail-form-group">
                <label>Email</label>
                <div className="mail-email-input">
                  <input
                    type="text"
                    value={accountForm.email}
                    onChange={(e) => setAccountForm({...accountForm, email: e.target.value})}
                    placeholder="kullanici"
                    disabled={!!editingAccount}
                  />
                  <span>@kralsosyal.com</span>
                </div>
              </div>
              <div className="mail-form-group">
                <label>Şifre {editingAccount && '(boş = değişmez)'}</label>
                <input
                  type="password"
                  value={accountForm.password}
                  onChange={(e) => setAccountForm({...accountForm, password: e.target.value})}
                  required={!editingAccount}
                />
              </div>
              <div className="mail-form-group">
                <label>Görünen Ad</label>
                <input
                  type="text"
                  value={accountForm.display_name}
                  onChange={(e) => setAccountForm({...accountForm, display_name: e.target.value})}
                  placeholder="Destek Ekibi"
                />
              </div>
              <div className="mail-form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={accountForm.auto_reply_enabled}
                    onChange={(e) => setAccountForm({...accountForm, auto_reply_enabled: e.target.checked})}
                  />
                  Auto-Reply Aktif
                </label>
              </div>
              <div className="mail-modal-actions">
                <button type="button" onClick={() => setShowAccountModal(false)}>İptal</button>
                <button type="submit" className="primary">{editingAccount ? 'Güncelle' : 'Oluştur'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* TEMPLATE MODAL */}
      {showTemplateModal && (
        <div className="mail-modal-overlay" onClick={() => setShowTemplateModal(false)}>
          <div className="mail-modal-content large" onClick={e => e.stopPropagation()}>
            <div className="mail-modal-header">
              <h3>{editingTemplate ? 'Şablon Düzenle' : 'Yeni Şablon'}</h3>
              <button className="mail-modal-close" onClick={() => setShowTemplateModal(false)}>×</button>
            </div>
            <form onSubmit={handleTemplateSubmit}>
              <div className="mail-form-group">
                <label>Mail Hesabı</label>
                <select
                  value={templateForm.mail_account_id}
                  onChange={(e) => setTemplateForm({...templateForm, mail_account_id: e.target.value})}
                  required
                >
                  <option value="">Seçiniz...</option>
                  {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.email}</option>
                  ))}
                </select>
              </div>
              <div className="mail-form-group">
                <label>Yanıt Konusu</label>
                <input
                  type="text"
                  value={templateForm.subject}
                  onChange={(e) => setTemplateForm({...templateForm, subject: e.target.value})}
                />
                <small>{'{{original_subject}}'} = orijinal konu</small>
              </div>
              <div className="mail-form-group">
                <label>Yanıt İçeriği</label>
                <textarea
                  value={templateForm.body}
                  onChange={(e) => setTemplateForm({...templateForm, body: e.target.value})}
                  rows={6}
                  required
                />
              </div>
              <div className="mail-form-row">
                <label><input type="checkbox" checked={templateForm.is_html} onChange={(e) => setTemplateForm({...templateForm, is_html: e.target.checked})} /> HTML</label>
                <label><input type="checkbox" checked={templateForm.is_active} onChange={(e) => setTemplateForm({...templateForm, is_active: e.target.checked})} /> Aktif</label>
              </div>
              <div className="mail-modal-actions">
                <button type="button" onClick={() => setShowTemplateModal(false)}>İptal</button>
                <button type="submit" className="primary">{editingTemplate ? 'Güncelle' : 'Oluştur'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MailManagementTab;